import { FC, Fragment, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { Formik } from "formik"
import * as Yup from "yup"
import Form from "../form"
import { useCreateBrandAdminMutation } from "../../../redux/queries/loan-officer"
import { useFetchBrandsQuery } from "../../../redux/queries/brand"
import { useToast } from "../../hooks"

interface Props {
  open: boolean
  onClose: () => void
  brandId?: string
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required")
})

const CreateBrandAdminModal: FC<Props> = ({ open, onClose, brandId }) => {
  const [createBrandAdmin, { isLoading }] = useCreateBrandAdminMutation()
  const { data: brands = [], isLoading: isLoadingBrands } = useFetchBrandsQuery(
    {},
    { skip: !!brandId }
  )
  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const { pushSuccessToast, pushErrorToast } = useToast()

  const effectiveBrandId = brandId || selectedBrandId

  const handleSubmit = async (values: {
    email: string
    first_name: string
    last_name: string
  }) => {
    if (!effectiveBrandId) {
      pushErrorToast("Please select a brand")
      return
    }

    try {
      const result = await createBrandAdmin({
        brand_id: parseInt(effectiveBrandId),
        ...values
      }).unwrap()

      if (result.status === "error") {
        pushErrorToast(result.message || "Failed to create brand admin")
        return
      }

      pushSuccessToast("Brand admin created successfully")
      setSelectedBrandId("")
      onClose()
    } catch (err: any) {
      const status = err?.status || err?.originalStatus
      if (status === 422) {
        pushErrorToast("A user with this email already exists")
      } else {
        pushErrorToast(
          err?.data?.message || err?.message || "Failed to create brand admin"
        )
      }
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all rounded-md sm:my-8 sm:w-full sm:max-w-lg">
                <Formik
                  initialValues={{
                    email: "",
                    first_name: "",
                    last_name: ""
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ handleSubmit: formikSubmit }) => (
                    <form onSubmit={formikSubmit}>
                      <div className="bg-white px-4 pt-5 pb-4 sm:p-8 sm:pb-4">
                        <Dialog.Title
                          as="h3"
                          className="text-xl font-medium leading-6 text-base-dark"
                        >
                          Add Brand Admin
                        </Dialog.Title>
                        <div className="mt-6 space-y-4">
                          {!brandId && (
                            <Form.Select
                              data={brands}
                              selected={brands.find(
                                b => b.id === selectedBrandId
                              )}
                              setSelected={(id: string) =>
                                setSelectedBrandId(id)
                              }
                              label="Brand"
                              required
                              isLoading={isLoadingBrands}
                            />
                          )}
                          <Form.Input
                            name="first_name"
                            label="First Name"
                            placeholder="Enter first name"
                            required
                          />
                          <Form.Input
                            name="last_name"
                            label="Last Name"
                            placeholder="Enter last name"
                            required
                          />
                          <Form.Input
                            name="email"
                            label="Email"
                            type="email"
                            placeholder="Enter email address"
                            required
                          />
                        </div>
                      </div>
                      <hr className="text-gray-300 mt-4" />
                      <div className="bg-white px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="inline-flex justify-center items-center rounded-md border border-transparent bg-primary-400 hover:bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-0 sm:ml-3 sm:w-auto sm:text-md disabled:opacity-50"
                        >
                          {isLoading ? "Creating..." : "Create Admin"}
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex justify-center items-center rounded-md bg-gray-100 px-4 py-2 text-default font-medium text-gray-700 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-0 sm:mt-0 sm:ml-3 sm:w-auto sm:text-md"
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default CreateBrandAdminModal
